import ReactAnd from 'react-and';
import { Button } from '../../../../../../components/Clickables/Buttons/Button';
import { InternalLink } from '../../../../../../components/Clickables/InternalLink/InternalLink';
import { IFAQData } from '../../../../../../components/FAQ/FAQ';
import { getFishingWaterUrl, getSpeciesUrl } from '@domain/routes/urls';
import {
  RegionDetailFields,
  RegionTopWaterNodeFields,
  SpeciesByGroupNodeFields,
  WaterTypes,
} from '../../../../../../generated/graphql';
import { firstNonEmpty } from '@lib/strings/firstNonEmpty';

interface IProps {
  handleCTAClick: () => void;
  region: RegionDetailFields;
  speciesGroupName: string;
  species: SpeciesByGroupNodeFields[];
  topFishingWaters: RegionTopWaterNodeFields[];
}

const NUMBER_OF_FISHING_WATERS_TO_SHOW = 3;
const NUMBER_OF_SPECIES_TO_SHOW = 5;

const WATER_TYPE_PLURAL: Record<WaterTypes, string | null> = {
  [WaterTypes.lake]: 'lakes',
  [WaterTypes.stream]: 'rivers and streams',
  [WaterTypes.canal]: 'canals',
  [WaterTypes.ocean]: 'coastal waters',
  [WaterTypes.inshore]: 'inshore waters',
  [WaterTypes.man_made]: 'marinas, harbors and piers',
  [WaterTypes.reef]: 'reefs',
  [WaterTypes.swamp]: 'swamps and wetlands',
  [WaterTypes.unknown]: null,
};

const getWaterTypesString = (waters: RegionTopWaterNodeFields[]): string => {
  const seen = new Set<string>();
  const phrases: string[] = [];
  let hasUnknown = false;
  for (const { descriptionOfWaterType } of waters) {
    const phrase = WATER_TYPE_PLURAL[descriptionOfWaterType];
    if (phrase && !seen.has(phrase)) {
      seen.add(phrase);
      phrases.push(phrase);
    } else if (!phrase) {
      hasUnknown = true;
    }
  }
  if (phrases.length === 0) {
    return 'a variety of waters';
  }
  return hasUnknown ? `${phrases.join(', ')}, and other waters` : phrases.join(', ');
};

export const getFAQData = ({
  handleCTAClick,
  region,
  speciesGroupName,
  species,
  topFishingWaters,
}: IProps): IFAQData[] => {
  const regionName = firstNonEmpty(region.localName, region.name) || '';
  const speciesToShow = species.slice(0, NUMBER_OF_SPECIES_TO_SHOW);
  const watersToShow = topFishingWaters.slice(0, NUMBER_OF_FISHING_WATERS_TO_SHOW);
  const waterTypesString = getWaterTypesString(topFishingWaters);

  return [
    ...(species.length > 0
      ? [
          {
            id: '1',
            title: `🎣 What ${speciesGroupName} species can you catch in ${regionName}?`,
            body: (
              <>
                {`Anglers in ${regionName} commonly catch several ${speciesGroupName} species, including `}
                <ReactAnd>
                  {speciesToShow.map(({ _id, displayName }) => (
                    <InternalLink
                      key={`species-${_id}`}
                      to={getSpeciesUrl(_id, displayName)}
                      className="body-text-sm text-primary"
                    >
                      {displayName}
                    </InternalLink>
                  ))}
                </ReactAnd>
                {`, according to reports from Fishbrain members.`}
                <br />
                <br />
                {`These species can be found in many ${waterTypesString} throughout the area. If you are targeting a specific species, you can use the `}
                <Button
                  onClick={handleCTAClick}
                  size="sm"
                  className="body-text-sm text-primary font-semibold"
                  variant="textNone"
                >
                  Fishbrain app
                </Button>
                {` to filter catches by species and see where anglers are catching them and which baits they are using.`}
              </>
            ),
            bodyString: `Anglers in ${regionName} commonly catch several ${speciesGroupName} species, including ${speciesToShow
              .map(
                ({ _id, displayName }) =>
                  `<a href="${getSpeciesUrl(_id, displayName)}">${displayName}</a>`,
              )
              .join(
                ', ',
              )}, according to reports from Fishbrain members. These species can be found in many ${waterTypesString} throughout the area. If you are targeting a specific species, you can use the Fishbrain app to filter catches by species and see where anglers are catching them and which baits they are using.`,
          },
        ]
      : []),
    ...(topFishingWaters.length > 0
      ? [
          {
            id: '2',
            title: `📍 Where are the best places to catch ${speciesGroupName} in ${regionName}?`,
            body: (
              <>
                {`${speciesGroupName} are caught in ${waterTypesString} throughout ${regionName}. Popular fishing waters reported by Fishbrain members include `}
                <ReactAnd>
                  {watersToShow.map(({ _id, displayName }) => (
                    <InternalLink
                      key={`water-${_id}`}
                      to={getFishingWaterUrl(_id, displayName)}
                      className="body-text-sm text-primary"
                    >
                      {displayName}
                    </InternalLink>
                  ))}
                </ReactAnd>
                {`.`}
                <br />
                <br />
                {`The `}
                <Button
                  onClick={handleCTAClick}
                  size="sm"
                  className="body-text-sm text-primary font-semibold"
                  variant="textNone"
                >
                  Fishbrain app
                </Button>
                {` allows anglers to see exact catch locations shared by other members, helping you discover productive fishing spots where ${speciesGroupName} have recently been caught.`}
              </>
            ),
            bodyString: `${speciesGroupName} are caught in ${waterTypesString} throughout ${regionName}. Popular fishing waters reported by Fishbrain members include ${watersToShow
              .map(
                ({ _id, displayName }) =>
                  `<a href="${getFishingWaterUrl(_id, displayName)}">${displayName}</a>`,
              )
              .join(
                ', ',
              )}. The Fishbrain app allows anglers to see exact catch locations shared by other members, helping you discover productive fishing spots where ${speciesGroupName} have recently been caught.`,
          },
        ]
      : []),
  ];
};
